import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { breadcrumbJsonLd, seo } from "@/lib/seo";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Eyebrow } from "@/components/Eyebrow";
import { Aurora } from "@/components/Aurora";
import { PagePlaceholder } from "@/components/site/PagePlaceholder";
import { CategoryChips, PostList } from "@/components/blog/PostList";
import { CATEGORY_LABELS, getPostsByCategory, type PostCategory } from "@/content/posts";

const CATEGORY_DESCRIPTIONS: Record<PostCategory, string> = {
  "getting-started": "Articles for a small business deciding whether, and where, to start with AI.",
  automation: "Articles on automating repetitive work in a small business, with and without AI.",
  adoption: "Articles on getting a team to actually use the AI systems you built or bought.",
};

// The visible intro above is one sentence; meta descriptions need 120–155 characters.
const CATEGORY_META_DESCRIPTIONS: Record<PostCategory, string> = {
  "getting-started":
    "Articles for a small business deciding whether, and where, to start with AI: readiness, what an assessment finds, and the first task to pick.",
  automation:
    "Articles on automating repetitive work in a small business, with and without AI: what to automate first, and when a plain rule is enough.",
  adoption:
    "Articles on getting a team to actually use the AI systems you built or bought: why usage decays, what adoption work is, and a usage policy.",
};

function isCategory(value: string): value is PostCategory {
  return Object.prototype.hasOwnProperty.call(CATEGORY_LABELS, value);
}

export const Route = createFileRoute("/_site/blog/category/$category")({
  head: ({ params }) => {
    const category = isCategory(params.category) ? params.category : undefined;
    if (!category) {
      return seo({
        title: "Category",
        description: "Articles from LAA Concierge Consulting in this category.",
        path: `/blog/category/${params.category}`,
        noindex: true,
      });
    }
    const label = CATEGORY_LABELS[category];
    return seo({
      title: `${label}: AI Articles`,
      description: CATEGORY_META_DESCRIPTIONS[category],
      path: `/blog/category/${category}`,
      jsonLd: breadcrumbJsonLd([
        { name: "Articles", path: "/blog" },
        { name: label, path: `/blog/category/${category}` },
      ]),
    });
  },
  loader: ({ params }) => {
    if (!isCategory(params.category)) throw notFound();
    return { category: params.category };
  },
  component: Page,
  errorComponent: () => (
    <PagePlaceholder title="This archive didn't load" intro="Try refreshing the page." />
  ),
  notFoundComponent: () => (
    <PagePlaceholder title="Category not found" intro="This category doesn't exist or was moved." />
  ),
});

function Page() {
  const params = Route.useParams();
  const category = params.category as PostCategory;
  const label = CATEGORY_LABELS[category];
  const posts = getPostsByCategory(category);

  return (
    <>
      <Section tone="paper" eager className="relative overflow-hidden">
        <Aurora variant="light" className="opacity-70" />
        <Container className="relative">
          <nav aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-eyebrow uppercase text-ink-500">
              <li>
                <Link
                  to="/"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-sm text-brand-700 transition-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link
                  to="/blog"
                  className="inline-flex min-h-[44px] items-center rounded-sm text-brand-700 transition-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
                >
                  Articles
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page">{label}</li>
            </ol>
          </nav>

          <div className="mt-8">
            <Eyebrow>Articles</Eyebrow>
            <h1
              className="mt-5 max-w-[20ch] font-display text-display-lg text-ink-900"
              style={{ textWrap: "balance" }}
            >
              {label}: articles
            </h1>
          </div>
          <p className="mt-6 max-w-[60ch] text-body-lg text-ink-600">
            {CATEGORY_DESCRIPTIONS[category]}
          </p>
          <CategoryChips active={category} />
        </Container>
      </Section>

      <Section tone="raised">
        <Container>
          <PostList posts={posts} />
        </Container>
      </Section>
    </>
  );
}
